import {
  Component,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import { fetchCharacterVoices, postCharacterVoice } from "@/api/characterVoices";
import {
  fetchVoiceSuggest,
  fetchVoices,
  voiceLanguage,
  type VoiceItem,
  type VoiceSuggestCastParams,
  type VoiceSuggestItem,
} from "@/api/voices";
import {
  emptyVoiceAssignmentsRow,
  getCastAnalysisRow,
  getVoiceAssignmentsRow,
  putVoiceAssignmentsRow,
} from "@/lib/importedBookDb";
import type {
  BookCastAnalysisRow,
  BookCastJson,
  BookVoiceAssignmentsRow,
  CastCharacter,
  ManualVoiceAssignment,
} from "@/lib/castAnalysisTypes";
import { CAST_ANALYSIS_UPDATED_EVENT, retryCastAnalysis } from "@/lib/castAnalysisTrigger";
import { mergeServerCharacterVoices } from "@/lib/mergeServerCharacterVoices";
import { DEFAULT_VOICE_SETTINGS } from "@/lib/mockElevenVoices";
import { requestVoicePreview } from "@/lib/requestVoicePreview";
import { sampleTextForVoicePreview } from "@/lib/voicePreviewSample";

type Props = {
  bookId: string;
  bookTitle: string;
  author: string;
  castAnalysisEligible: boolean;
};

function FieldBlock(props: { label: string; value: string }) {
  const { label, value } = props;
  if (!value.trim()) return null;
  return (
    <div className="mt-2">
      <p className="text-[0.58rem] font-semibold uppercase tracking-[0.1em] text-[#8a7a68]" style={{ fontFamily: "'Inter', sans-serif" }}>
        {label}
      </p>
      <p className="mt-0.5 text-xs leading-relaxed text-[#d4c4a8]" style={{ fontFamily: "'Lora', serif" }}>
        {value}
      </p>
    </div>
  );
}

function manualAssignmentFromVoiceItem(character: string, v: VoiceItem): ManualVoiceAssignment {
  const label = typeof v.provider_name === "string" && v.provider_name.trim() ? v.provider_name.trim() : v.voice_id;
  return {
    character,
    voice_id: v.voice_id,
    voice_name: label,
    voice_settings: { ...DEFAULT_VOICE_SETTINGS },
    client_updated_at: new Date().toISOString(),
  };
}

const VOICE_SEARCH_MIN = 2;
const VOICE_SEARCH_LIMIT = 10;
const VOICE_SEARCH_DEBOUNCE_MS = 280;

/** First-pass catalog search text from cast `voice_direction` (no ranking AI). */
function clampVoiceDirectionSeed(raw: string | null | undefined): string {
  if (!raw || typeof raw !== "string") return "";
  const t = raw.replace(/\s+/g, " ").trim();
  if (!t) return "";
  return t.slice(0, 160);
}

function isVoiceSuggestItem(v: VoiceItem): v is VoiceSuggestItem {
  return (
    typeof (v as VoiceSuggestItem).match_reason === "string" &&
    typeof (v as VoiceSuggestItem).match_score === "number"
  );
}

function VoiceAssignControls(props: {
  bookId: string;
  characterName: string;
  isNarrator: boolean;
  searchInputId: string;
  /** When cast analysis supplies `voice_direction`, seed the catalog search (feels instant). */
  voiceSearchSeed?: string | null;
  /** Cast fields for GET /api/voices/suggest when the user is still on the seeded direction text. */
  suggestContext?: VoiceSuggestCastParams | null;
  currentVoiceId: string;
  assignment: ManualVoiceAssignment | null | undefined;
  onSelectVoice: (assignment: ManualVoiceAssignment) => void;
  onClearVoice: () => void;
}) {
  const {
    bookId,
    characterName,
    isNarrator,
    searchInputId,
    voiceSearchSeed,
    suggestContext,
    currentVoiceId,
    assignment,
    onSelectVoice,
    onClearVoice,
  } = props;
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [results, setResults] = useState<VoiceItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const seed = clampVoiceDirectionSeed(voiceSearchSeed);
    if (!seed || seed.length < VOICE_SEARCH_MIN) return;
    setSearch((prev) => (prev.trim().length === 0 ? seed : prev));
    setDebounced((prev) => (prev.trim().length === 0 ? seed : prev));
  }, [voiceSearchSeed]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), VOICE_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [search]);

  const seedKey = useMemo(() => clampVoiceDirectionSeed(voiceSearchSeed).trim().toLowerCase(), [voiceSearchSeed]);

  const intentSearchActive = useMemo(() => {
    if (!suggestContext?.character_name?.trim()) return false;
    if (seedKey.length < VOICE_SEARCH_MIN) return false;
    return debounced.trim().toLowerCase() === seedKey;
  }, [suggestContext, seedKey, debounced]);

  useEffect(() => {
    if (debounced.length < VOICE_SEARCH_MIN) {
      setResults([]);
      setLoadError(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    setLoadError(null);

    const runLiteral = () =>
      fetchVoices({ search: debounced, limit: VOICE_SEARCH_LIMIT, offset: 0 }).then((r) => {
        if (!cancelled) setResults(r.items);
      });

    const runSuggest = () => {
      const ctx = suggestContext!;
      const payload: VoiceSuggestCastParams = {
        character_name: ctx.character_name?.trim() || characterName,
        role: ctx.role,
        description: ctx.description,
        personality: ctx.personality,
        speaking_style: ctx.speaking_style,
        age_vocal_quality: ctx.age_vocal_quality,
        voice_direction: ctx.voice_direction ?? voiceSearchSeed ?? undefined,
        gender: ctx.gender,
        limit: VOICE_SEARCH_LIMIT,
      };
      return fetchVoiceSuggest(payload).then((r) => {
        if (!cancelled) setResults(r.items);
      });
    };

    void (intentSearchActive ? runSuggest() : runLiteral())
      .catch((e) => {
        if (!cancelled) {
          setResults([]);
          setLoadError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, intentSearchActive, suggestContext, characterName, voiceSearchSeed]);

  const canPreview = Boolean(assignment?.voice_id);

  const runPreview = useCallback(async () => {
    if (!assignment?.voice_id) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const sample = sampleTextForVoicePreview(characterName, isNarrator);
      const res = await requestVoicePreview({
        bookId,
        characterName,
        assignment,
        sampleText: sample,
      });
      if (!res.success) {
        setPreviewAudioUrl(null);
        setPreviewError(res.error || "Preview failed");
        return;
      }
      setPreviewAudioUrl(res.audio_url);
      queueMicrotask(() => {
        void audioRef.current?.play().catch(() => {
          setPreviewError("Could not start playback (use the player controls).");
        });
      });
    } catch (e) {
      setPreviewAudioUrl(null);
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [assignment, bookId, characterName, isNarrator]);

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <label
        htmlFor={searchInputId}
        className="text-[0.58rem] font-semibold uppercase tracking-[0.1em] text-[#8a7a68]"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {intentSearchActive
          ? "Voice matches (intent from cast)"
          : `Search voices (catalog)${voiceSearchSeed?.trim() ? " — started from voice direction" : ""}`}
      </label>
      <input
        id={searchInputId}
        type="search"
        autoComplete="off"
        placeholder={
          intentSearchActive
            ? "Edit text to search the catalog literally…"
            : `Type at least ${VOICE_SEARCH_MIN} characters…`
        }
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPreviewAudioUrl(null);
          setPreviewError(null);
        }}
        className="w-full max-w-md rounded border border-[#4a3820]/80 bg-[#0a0806] px-2 py-1.5 text-[0.7rem] text-[#e8dcc8] outline-none ring-amber-900/30 focus:ring-1"
        style={{ fontFamily: "'Inter', sans-serif" }}
      />
      {searching ? (
        <p className="text-[0.65rem] text-[#8a7a68]" style={{ fontFamily: "'Inter', sans-serif" }}>
          Searching…
        </p>
      ) : null}
      {loadError ? (
        <p className="text-[0.65rem] leading-snug text-amber-200/90" style={{ fontFamily: "'Inter', sans-serif" }}>
          {loadError}
        </p>
      ) : null}
      {debounced.length >= VOICE_SEARCH_MIN && !searching && results.length === 0 && !loadError ? (
        <p className="text-[0.65rem] text-[#6a5c4e]" style={{ fontFamily: "'Inter', sans-serif" }}>
          {intentSearchActive
            ? "No suggestions returned. Try editing the search for a literal catalog lookup."
            : "No catalog matches. Try a different spelling or shorter fragment."}
        </p>
      ) : null}
      {results.length > 0 ? (
        <ul className="mt-1 flex max-h-56 flex-col gap-1.5 overflow-y-auto pr-0.5" aria-label="Voice search results">
          {results.map((v) => {
            const lang = voiceLanguage(v.labels);
            const selected = currentVoiceId === v.voice_id;
            const assign = () => {
              setPreviewAudioUrl(null);
              setPreviewError(null);
              onSelectVoice(manualAssignmentFromVoiceItem(characterName, v));
            };
            return (
              <li key={v.voice_id}>
                <div
                  className={`rounded border px-2 py-1.5 ${
                    selected
                      ? "border-[#b8921f]/70 bg-[#1a140c] ring-1 ring-amber-600/25"
                      : "border-[#4a3820]/50 bg-[#0f0c09]/90"
                  }`}
                >
                  <p className="text-[0.72rem] font-medium text-[#e8dcc8]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {v.provider_name}
                  </p>
                  <p className="mt-0.5 text-[0.62rem] text-[#9a8a78]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {[v.gender, v.accent, lang].filter(Boolean).join(" · ") || "—"}
                  </p>
                  {isVoiceSuggestItem(v) ? (
                    <p
                      className="mt-1 text-[0.58rem] leading-snug text-[#6a5c4e]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      title={v.match_reason}
                    >
                      <span className="font-medium text-[#7d6d58]">Score {v.match_score.toFixed(2)}</span>
                      {v.match_reason ? (
                        <>
                          {" · "}
                          {v.match_reason}
                        </>
                      ) : null}
                    </p>
                  ) : null}
                  {v.preview_url ? (
                    <audio
                      className="mt-1.5 h-9 w-full max-w-[min(100%,320px)]"
                      controls
                      preload="none"
                      src={v.preview_url}
                    />
                  ) : null}
                  <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      className="rounded border border-[#8a6a2a]/85 bg-gradient-to-b from-[#4a3818] to-[#22180c] px-3 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.06em] text-[#fdf6e9] shadow-sm transition-colors hover:from-[#5c461c] hover:to-[#2a1e10]"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      onClick={assign}
                    >
                      Assign
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          title={canPreview ? "Generate a short preview clip (n8n)" : "Pick a voice from search results first"}
          className="shrink-0 rounded border border-[#5c4a2a]/70 bg-[#161008] px-2 py-1.5 text-[0.62rem] font-medium uppercase tracking-[0.06em] text-[#c4b49a] transition-colors enabled:hover:border-[#b8921f]/55 enabled:hover:text-[#f0e6d4] disabled:cursor-not-allowed disabled:opacity-40"
          style={{ fontFamily: "'Inter', sans-serif" }}
          disabled={!canPreview || previewLoading}
          onClick={() => void runPreview()}
        >
          {previewLoading ? "Loading…" : "Preview voice (n8n)"}
        </button>
        <button
          type="button"
          className="shrink-0 rounded border border-[#5c3a2a]/60 bg-[#120e0a] px-2 py-1.5 text-[0.62rem] font-medium uppercase tracking-[0.06em] text-[#a89880] transition-colors hover:border-amber-800/50 hover:text-[#f0e6d4]"
          style={{ fontFamily: "'Inter', sans-serif" }}
          disabled={!currentVoiceId}
          onClick={() => {
            setPreviewAudioUrl(null);
            setPreviewError(null);
            onClearVoice();
          }}
        >
          Clear voice
        </button>
      </div>
      {previewError ? (
        <p className="text-[0.65rem] leading-snug text-amber-200/90" style={{ fontFamily: "'Inter', sans-serif" }}>
          {previewError}
        </p>
      ) : null}
      {previewAudioUrl ? (
        <audio
          ref={audioRef}
          key={previewAudioUrl}
          className="mt-1 h-9 w-full max-w-md rounded border border-[#4a3820]/60 bg-[#0a0806]"
          controls
          src={previewAudioUrl}
        />
      ) : null}
    </div>
  );
}

function CharacterCard(props: {
  bookId: string;
  character: CastCharacter;
  cardIndex: number;
  manualVoice?: ManualVoiceAssignment | null;
  onVoiceChange: (characterName: string, assignment: ManualVoiceAssignment | null) => void;
}) {
  const { bookId, character: c, cardIndex, manualVoice, onVoiceChange } = props;
  const name = typeof c.name === "string" && c.name.trim() ? c.name.trim() : "Unnamed character";
  const rel = Array.isArray(c.relationships) && c.relationships.length > 0 ? c.relationships.join(", ") : "";
  const voiceLine = c.voice_direction?.trim() ? `Voice direction: ${c.voice_direction.trim()}` : "";
  const fromCast = c.voice;
  const assignedId = manualVoice?.voice_id ?? fromCast?.voice_id ?? "";
  const assignedLabel =
    (manualVoice?.voice_name && manualVoice?.voice_id ? manualVoice.voice_name : null) ??
    (fromCast?.voice_name || fromCast?.voice_id ? (fromCast?.voice_name ?? fromCast?.voice_id ?? "") : "");
  const voiceAssign = assignedLabel ? `Assigned: ${assignedLabel}` : "Not assigned yet";

  return (
    <article
      className="rounded-lg border border-[#4a3820]/70 bg-[#110d0a]/88 p-3 shadow-inner"
      aria-label={`Character ${name}`}
    >
      <h4 className="text-sm font-semibold text-[#f0e6d4]" style={{ fontFamily: "'Playfair Display', serif" }}>
        {name}
      </h4>
      {c.role ? (
        <p className="mt-1 text-[0.65rem] font-medium uppercase tracking-[0.08em] text-[#b8a68a]" style={{ fontFamily: "'Inter', sans-serif" }}>
          {c.role}
        </p>
      ) : null}
      {Array.isArray(c.aliases) && c.aliases.length > 0 ? (
        <p className="mt-1 text-[0.62rem] italic leading-snug text-[#8a7a68]" style={{ fontFamily: "'Inter', sans-serif" }}>
          Also appears as: {c.aliases.join(", ")}
        </p>
      ) : null}
      {c.cast_category || c.importance ? (
        <p className="mt-0.5 text-[0.55rem] uppercase tracking-[0.12em] text-[#6a5c4e]" style={{ fontFamily: "'Inter', sans-serif" }}>
          {[c.cast_category, c.importance].filter(Boolean).join(" · ")}
        </p>
      ) : null}
      <FieldBlock label="Description" value={c.description ?? ""} />
      <FieldBlock label="Backstory" value={c.backstory ?? ""} />
      <FieldBlock label="Personality" value={c.personality ?? ""} />
      {rel ? <FieldBlock label="Relationships" value={rel} /> : null}
      <FieldBlock label="Speaking style" value={c.speaking_style ?? ""} />
      <FieldBlock label="Age / vocal quality" value={c.age_or_vocal_quality ?? ""} />
      {voiceLine ? (
        <p className="mt-2 text-xs italic leading-relaxed text-[#c4b49a]" style={{ fontFamily: "'Lora', serif" }}>
          {voiceLine}
        </p>
      ) : null}
      <p className="mt-2 text-[0.65rem] text-[#7d8f6a]" style={{ fontFamily: "'Inter', sans-serif" }}>
        {voiceAssign}
      </p>
      <VoiceAssignControls
        key={`va-${bookId}-${name}-${cardIndex}`}
        bookId={bookId}
        characterName={name}
        isNarrator={false}
        searchInputId={`cast-voice-char-search-${cardIndex}`}
        voiceSearchSeed={c.voice_direction}
        suggestContext={{
          character_name: name,
          role: typeof c.role === "string" ? c.role : undefined,
          description: typeof c.description === "string" ? c.description : undefined,
          personality: typeof c.personality === "string" ? c.personality : undefined,
          speaking_style: typeof c.speaking_style === "string" ? c.speaking_style : undefined,
          age_vocal_quality: typeof c.age_or_vocal_quality === "string" ? c.age_or_vocal_quality : undefined,
          voice_direction: typeof c.voice_direction === "string" ? c.voice_direction : undefined,
        }}
        currentVoiceId={typeof assignedId === "string" ? assignedId : ""}
        assignment={manualVoice ?? undefined}
        onSelectVoice={(a) => onVoiceChange(name, a)}
        onClearVoice={() => onVoiceChange(name, null)}
      />
      {typeof c.confidence === "number" && Number.isFinite(c.confidence) ? (
        <p className="mt-1 text-[0.58rem] text-[#6a5c4e]" style={{ fontFamily: "'Inter', sans-serif" }}>
          Confidence: {Math.round(Math.min(1, Math.max(0, c.confidence)) * 100)}%
        </p>
      ) : null}
      {Array.isArray(c.source_chunks) && c.source_chunks.length > 0 ? (
        <FieldBlock
          label="Seen in text chunks"
          value={c.source_chunks.filter((s): s is string => typeof s === "string" && s.trim().length > 0).join(", ")}
        />
      ) : null}
    </article>
  );
}

function isBookCastJson(doc: unknown): doc is BookCastJson {
  return typeof doc === "object" && doc !== null && "success" in doc;
}

function safeCharactersList(doc: unknown): CastCharacter[] {
  if (!isBookCastJson(doc) || doc.success !== true) return [];
  const raw = doc.characters;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (c): c is CastCharacter =>
      c != null &&
      typeof c === "object" &&
      typeof (c as CastCharacter).name === "string" &&
      (c as CastCharacter).name.trim().length > 0,
  ) as CastCharacter[];
}

type VoiceServerSyncState = "idle" | "loading" | "ok" | "error";

function CastCharactersPanelImpl(props: Props) {
  const { bookId, bookTitle, author, castAnalysisEligible } = props;
  const [row, setRow] = useState<BookCastAnalysisRow | undefined>(undefined);
  const [voiceRow, setVoiceRow] = useState<BookVoiceAssignmentsRow | undefined>(undefined);
  const [initialized, setInitialized] = useState(false);
  const [voiceServerStatus, setVoiceServerStatus] = useState<VoiceServerSyncState>("idle");
  const [voiceServerError, setVoiceServerError] = useState<string | null>(null);

  const voiceRefreshGenRef = useRef(0);

  const persistVoiceRow = useCallback(
    async (
      next: BookVoiceAssignmentsRow,
      serverCharacterId?: string | null,
      serverVoiceId?: string | null,
    ) => {
      const stamped: BookVoiceAssignmentsRow = { ...next, bookId, updatedAt: new Date().toISOString() };
      setVoiceRow(stamped);
      try {
        await putVoiceAssignmentsRow(stamped);
      } catch (e) {
        console.warn("[cast-voices] IndexedDB save failed", { bookId, error: e });
      }
      if (serverCharacterId && serverVoiceId) {
        try {
          await postCharacterVoice({
            story_id: bookId,
            character_id: serverCharacterId,
            voice_id: serverVoiceId,
          });
        } catch (e) {
          console.warn("[cast-voices] POST /api/character-voices failed", {
            bookId,
            characterId: serverCharacterId,
            error: e,
          });
        }
      }
    },
    [bookId],
  );

  const refresh = useCallback(async () => {
    const refreshGen = ++voiceRefreshGenRef.current;

    let castRow: BookCastAnalysisRow | undefined;
    try {
      castRow = await getCastAnalysisRow(bookId);
    } catch (e) {
      console.warn("[cast-analysis] panel load row cast failed", { bookId, error: e });
    }

    if (voiceRefreshGenRef.current !== refreshGen) return;

    let narratorKey: string | null = null;
    if (castRow?.document && isBookCastJson(castRow.document) && castRow.document.success) {
      const n = castRow.document.narrator?.name;
      narratorKey = typeof n === "string" && n.trim() ? n.trim() : "Narrator";
    }

    setRow(castRow);
    setInitialized(true);

    let localVoices: BookVoiceAssignmentsRow;
    try {
      const v = await getVoiceAssignmentsRow(bookId);
      if (voiceRefreshGenRef.current !== refreshGen) return;
      localVoices = v ? { ...v, bookId } : emptyVoiceAssignmentsRow(bookId);
    } catch (e) {
      console.warn("[cast-analysis] panel load row voice failed", { bookId, error: e });
      localVoices = emptyVoiceAssignmentsRow(bookId);
    }

    setVoiceRow(localVoices);

    setVoiceServerStatus("loading");
    setVoiceServerError(null);
    try {
      const { items } = await fetchCharacterVoices(bookId);
      if (voiceRefreshGenRef.current !== refreshGen) return;

      let mergeBase: BookVoiceAssignmentsRow;
      try {
        const v = await getVoiceAssignmentsRow(bookId);
        if (voiceRefreshGenRef.current !== refreshGen) return;
        mergeBase = v ? { ...v, bookId } : emptyVoiceAssignmentsRow(bookId);
      } catch {
        mergeBase = localVoices;
      }

      const merged = mergeServerCharacterVoices(mergeBase, items, narratorKey);
      const before = JSON.stringify({ n: mergeBase.narrator, c: mergeBase.characters });
      const after = JSON.stringify({ n: merged.narrator, c: merged.characters });
      if (after !== before) {
        const stamped: BookVoiceAssignmentsRow = {
          ...merged,
          bookId,
          updatedAt: new Date().toISOString(),
        };
        await putVoiceAssignmentsRow(stamped);
        if (voiceRefreshGenRef.current !== refreshGen) return;
        setVoiceRow(stamped);
      } else {
        setVoiceRow(mergeBase);
      }
      setVoiceServerStatus("ok");
    } catch (e) {
      if (voiceRefreshGenRef.current !== refreshGen) return;
      const msg = e instanceof Error ? e.message : String(e);
      setVoiceServerError(msg);
      setVoiceServerStatus("error");
      try {
        const v = await getVoiceAssignmentsRow(bookId);
        if (voiceRefreshGenRef.current !== refreshGen) return;
        setVoiceRow(v ? { ...v, bookId } : emptyVoiceAssignmentsRow(bookId));
      } catch {
        setVoiceRow(localVoices);
      }
      console.warn("[cast-voices] GET /api/character-voices failed (using IndexedDB only)", { bookId, error: e });
    }

    console.info("[cast-analysis] panel load row", {
      bookId,
      analysisStatus: castRow?.analysisStatus ?? "missing",
      hasDocument: Boolean(castRow?.document),
      merge_pass_applied:
        castRow?.document && typeof castRow.document === "object" && "merge_pass_applied" in castRow.document
          ? (castRow.document as { merge_pass_applied?: boolean }).merge_pass_applied
          : undefined,
    });
  }, [bookId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onEvt = (e: Event) => {
      const d = (e as CustomEvent<{ bookId?: string }>).detail;
      if (d?.bookId === bookId) void refresh();
    };
    window.addEventListener(CAST_ANALYSIS_UPDATED_EVENT, onEvt);
    return () => window.removeEventListener(CAST_ANALYSIS_UPDATED_EVENT, onEvt);
  }, [bookId, refresh]);

  useEffect(() => {
    if (!castAnalysisEligible) return;
    if (row?.analysisStatus !== "queued" && row?.analysisStatus !== "processing") return;
    const id = window.setInterval(() => void refresh(), 2500);
    return () => clearInterval(id);
  }, [castAnalysisEligible, row?.analysisStatus, refresh]);

  useEffect(() => {
    if (!castAnalysisEligible) return;
    if (row !== undefined) return;
    if (!initialized) return;
    const id = window.setInterval(() => void refresh(), 2000);
    return () => clearInterval(id);
  }, [castAnalysisEligible, row, initialized, refresh]);

  const charactersList = useMemo(() => safeCharactersList(row?.document ?? null), [row?.document]);
  const visibleCharacters = useMemo(
    () =>
      charactersList.filter(
        (c) => c.cast_category !== "metadata_exclude" && c.cast_category !== "narrator",
      ),
    [charactersList],
  );

  if (!castAnalysisEligible) {
    return (
      <div
        role="tabpanel"
        id="companion-panel-characters"
        aria-labelledby="companion-tab-characters"
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-0.5"
      >
        <p className="text-xs leading-relaxed text-[#9a8a78]" style={{ fontFamily: "'Inter', sans-serif" }}>
          Cast analysis runs automatically for books stored in this browser (Discover import or .txt upload). Open a
          chapter from an imported title to see narrator and character cards here.
        </p>
      </div>
    );
  }

  if (!initialized) {
    return (
      <div
        role="tabpanel"
        id="companion-panel-characters"
        aria-labelledby="companion-tab-characters"
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-0.5"
      >
        <p className="text-xs text-[#bcae98]" style={{ fontFamily: "'Inter', sans-serif" }}>
          Loading cast index…
        </p>
      </div>
    );
  }

  const st = row?.analysisStatus ?? "missing";
  const doc = row?.document;
  const docOk = isBookCastJson(doc) && doc.success === true;

  const mergeErr =
    docOk && doc.analysis_mode === "full_book" && typeof doc.cast_merge_debug?.merge_error === "string"
      ? doc.cast_merge_debug.merge_error.trim()
      : "";
  const castBlurb =
    docOk && doc.analysis_mode === "full_book"
      ? doc.merge_pass_applied === false && mergeErr
        ? `Full-book cast (${typeof doc.chunks_analyzed === "number" ? doc.chunks_analyzed : "?"} segments). Canonical merge did not complete — ${mergeErr.slice(0, 200)}${mergeErr.length > 200 ? "…" : ""} Showing preliminary merged names.`
        : `Full-book cast index (${typeof doc.chunks_analyzed === "number" ? doc.chunks_analyzed : "?"} text segments).${doc.merge_pass_applied ? " Names deduplicated with an AI canonical pass." : ""} Later chapters are included when they appear in the source text.`
      : "Early cast preview based on an excerpt. Full-book character analysis coming soon.";

  const prog = row?.progress;
  const showChunkProgress =
    prog?.analysisMode === "full" &&
    typeof prog.chunksTotal === "number" &&
    Number.isFinite(prog.chunksTotal) &&
    prog.chunksTotal > 0 &&
    typeof prog.chunksReady === "number" &&
    Number.isFinite(prog.chunksReady);

  const narrator = docOk && doc.narrator && typeof doc.narrator === "object" ? doc.narrator : null;

  return (
    <div
      role="tabpanel"
      id="companion-panel-characters"
      aria-labelledby="companion-tab-characters"
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-0.5"
    >
      <div className="rounded-md border border-[#5c4a2a]/45 bg-[#0f0c09]/80 p-2.5">
        <p className="text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-[#9f917b]" style={{ fontFamily: "'Inter', sans-serif" }}>
          Cast index
        </p>
        <p className="mt-1 text-[0.65rem] leading-snug text-[#7d6d58]" style={{ fontFamily: "'Inter', sans-serif" }}>
          {castBlurb}
        </p>
        <p className="mt-1.5 text-[0.58rem] leading-snug text-[#5c5044]" style={{ fontFamily: "'Inter', sans-serif" }}>
          Voice picks are saved in this browser (IndexedDB). When the reader API is reachable, assignments also sync to
          Postgres (best-effort).
        </p>
        {voiceServerStatus === "loading" ? (
          <p className="mt-1.5 text-[0.65rem] text-[#9a8a78]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Loading voice assignments from server…
          </p>
        ) : null}
        {voiceServerStatus === "error" && voiceServerError ? (
          <p
            className="mt-1.5 text-[0.65rem] leading-snug text-amber-200/90"
            style={{ fontFamily: "'Inter', sans-serif" }}
            role="alert"
          >
            Could not load server voice assignments ({voiceServerError}). Using IndexedDB only.
          </p>
        ) : null}
        {docOk && Array.isArray(doc.cast_analysis_warnings) && doc.cast_analysis_warnings.length > 0 ? (
          <div
            className="mt-2 rounded border border-amber-800/50 bg-amber-950/25 px-2 py-1.5"
            role="status"
            aria-live="polite"
          >
            {doc.cast_analysis_warnings.map((w, idx) => (
              <p
                key={idx}
                className="text-[0.65rem] leading-snug text-amber-100/90"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {w}
              </p>
            ))}
          </div>
        ) : null}
      </div>

      {st === "queued" || st === "processing" ? (
        <p className="text-xs text-[#c9b89a]" style={{ fontFamily: "'Inter', sans-serif" }}>
          {showChunkProgress && prog ? (
            <>
              Analyzing cast across the book… segment {Math.min(prog.chunksReady + 1, prog.chunksTotal)} of{" "}
              {prog.chunksTotal}. This runs in the background and does not block reading.
            </>
          ) : (
            <>Analyzing cast… This runs in the background and does not block reading.</>
          )}
        </p>
      ) : null}

      {st === "error" ? (
        <div className="rounded-lg border border-amber-900/50 bg-[#1a1208]/90 p-3">
          <p className="text-xs text-amber-100/95" style={{ fontFamily: "'Inter', sans-serif" }}>
            {row?.errorMessage ?? (isBookCastJson(doc) ? doc.error : undefined) ?? "Cast analysis failed."}
          </p>
          <button
            type="button"
            className="mt-2 w-full rounded-md border border-[#8a6a2a]/80 bg-gradient-to-b from-[#4a3818] to-[#22180c] px-3 py-2 text-xs font-semibold text-[#fdf6e9] shadow-sm transition-colors hover:from-[#5c461c] hover:to-[#2a1e10]"
            style={{ fontFamily: "'Inter', sans-serif" }}
            onClick={() => {
              retryCastAnalysis({ bookId, title: bookTitle, author, source: row?.source });
              void refresh();
            }}
          >
            Retry analysis
          </button>
        </div>
      ) : null}

      {st === "missing" ? (
        <div className="rounded-lg border border-[#4a3820]/60 bg-[#110d0a]/85 p-3">
          <p className="text-xs leading-relaxed text-[#9a8a78]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Cast analysis not ready yet. It starts automatically when the book is imported; you can open the reader
            while it runs.
          </p>
          <button
            type="button"
            className="mt-2 w-full rounded-md border border-[#8a6a2a]/80 bg-gradient-to-b from-[#4a3818] to-[#22180c] px-3 py-2 text-xs font-semibold text-[#fdf6e9] shadow-sm transition-colors hover:from-[#5c461c] hover:to-[#2a1e10]"
            style={{ fontFamily: "'Inter', sans-serif" }}
            onClick={() => {
              retryCastAnalysis({ bookId, title: bookTitle, author, source: row?.source });
              void refresh();
            }}
          >
            Run cast analysis now
          </button>
        </div>
      ) : null}

      {docOk && narrator ? (
        <div className="border-b border-[#3d3228]/60 pb-2">
          <section className="flex flex-col gap-2" aria-label="Narrator">
            <h3 className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#bba98e]" style={{ fontFamily: "'Inter', sans-serif" }}>
              Narrator
            </h3>
            <p className="text-[0.58rem] leading-snug text-[#6a5c4e]" style={{ fontFamily: "'Inter', sans-serif" }}>
              Assign a catalog voice for the narrator line, then scroll to character cards below.
            </p>
            <article className="rounded-lg border border-[#4a3820]/70 bg-[#110d0a]/88 p-3 shadow-inner">
              <h4 className="text-sm font-semibold text-[#f0e6d4]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {typeof narrator.name === "string" && narrator.name.trim() ? narrator.name.trim() : "Narrator"}
              </h4>
              <FieldBlock label="Description" value={typeof narrator.description === "string" ? narrator.description : ""} />
              <FieldBlock label="Tone" value={typeof narrator.tone === "string" ? narrator.tone : ""} />
              <FieldBlock label="Voice direction" value={typeof narrator.voice_direction === "string" ? narrator.voice_direction : ""} />
              {(() => {
                const nLabel =
                  typeof narrator.name === "string" && narrator.name.trim() ? narrator.name.trim() : "Narrator";
                const manual = voiceRow?.narrator ?? null;
                const assignedId = manual?.voice_id ?? "";
                const line = manual?.voice_name ? `Assigned: ${manual.voice_name}` : "Not assigned yet";
                return (
                  <>
                    <p className="mt-2 text-[0.65rem] text-[#7d8f6a]" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {line}
                    </p>
                    <VoiceAssignControls
                      key={`va-${bookId}-narrator`}
                      bookId={bookId}
                      characterName={nLabel}
                      isNarrator
                      searchInputId="cast-voice-narrator-search"
                      voiceSearchSeed={typeof narrator.voice_direction === "string" ? narrator.voice_direction : null}
                      suggestContext={{
                        character_name: nLabel,
                        role: typeof narrator.tone === "string" ? narrator.tone : undefined,
                        description: typeof narrator.description === "string" ? narrator.description : undefined,
                        voice_direction:
                          typeof narrator.voice_direction === "string" ? narrator.voice_direction : undefined,
                      }}
                      currentVoiceId={assignedId}
                      assignment={manual ?? undefined}
                      onSelectVoice={(a) => {
                        const base = voiceRow ?? emptyVoiceAssignmentsRow(bookId);
                        void persistVoiceRow({ ...base, narrator: a }, nLabel, a.voice_id);
                      }}
                      onClearVoice={() => {
                        const base = voiceRow ?? emptyVoiceAssignmentsRow(bookId);
                        void persistVoiceRow({ ...base, narrator: null });
                      }}
                    />
                  </>
                );
              })()}
            </article>
          </section>
        </div>
      ) : null}

      {docOk && visibleCharacters.length > 0 ? (
        <section className="flex flex-col gap-2" aria-label="Characters">
          <h3 className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#bba98e]" style={{ fontFamily: "'Inter', sans-serif" }}>
            Characters
          </h3>
          <div className="flex flex-col gap-2.5">
            {visibleCharacters.map((c, i) => {
              const rowKey = typeof c.name === "string" ? c.name.trim() : "";
              return (
                <CharacterCard
                  key={`${c.name}-${i}`}
                  bookId={bookId}
                  cardIndex={i}
                  character={c}
                  manualVoice={rowKey ? voiceRow?.characters[rowKey] : undefined}
                  onVoiceChange={(characterName, assignment) => {
                    const base = voiceRow ?? emptyVoiceAssignmentsRow(bookId);
                    const characters = { ...base.characters };
                    if (assignment) characters[characterName] = assignment;
                    else delete characters[characterName];
                    void persistVoiceRow(
                      { ...base, characters },
                      assignment ? characterName : null,
                      assignment?.voice_id ?? null,
                    );
                  }}
                />
              );
            })}
          </div>
        </section>
      ) : docOk ? (
        <p className="text-xs text-[#8a7a68]" style={{ fontFamily: "'Inter', sans-serif" }}>
          {doc.analysis_mode === "full_book"
            ? "No named characters were inferred from the stored text."
            : "No named characters were inferred from the excerpt."}
        </p>
      ) : null}

      {st === "ready" && docOk ? (
        <button
          type="button"
          className="mt-1 w-full rounded-md border border-[#5c4a2a]/70 bg-[#140f0c] px-3 py-2 text-[0.65rem] font-semibold uppercase tracking-[0.08em] text-[#c4b49a] transition-colors hover:border-[#b8921f]/55 hover:text-[#f0e6d4]"
          style={{ fontFamily: "'Inter', sans-serif" }}
          onClick={() => {
            retryCastAnalysis({ bookId, title: bookTitle, author, source: row?.source });
            void refresh();
          }}
        >
          Re-run cast analysis
        </button>
      ) : null}
    </div>
  );
}

type BoundaryProps = { children: ReactNode };
type BoundaryState = { error: Error | null };

class CastCharactersPanelErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[CastCharactersPanel] render error", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          role="tabpanel"
          id="companion-panel-characters"
          aria-labelledby="companion-tab-characters"
          className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto rounded-lg border border-amber-900/40 bg-[#1a1208]/90 p-3 pr-0.5"
        >
          <p className="text-[0.62rem] font-semibold uppercase tracking-[0.12em] text-amber-200/90" style={{ fontFamily: "'Inter', sans-serif" }}>
            Characters tab
          </p>
          <p className="text-xs leading-relaxed text-amber-100/90" style={{ fontFamily: "'Inter', sans-serif" }}>
            Something went wrong displaying the cast index. Reading is unaffected.{" "}
            <span className="block pt-1 font-mono text-[0.65rem] text-amber-200/80">{this.state.error.message}</span>
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

export function CastCharactersPanel(props: Props) {
  return (
    <CastCharactersPanelErrorBoundary>
      <CastCharactersPanelImpl {...props} />
    </CastCharactersPanelErrorBoundary>
  );
}
